import { NavLink } from 'react-router-dom'
import { NAV_GROUPS, NAV_GROUP_META, ROUTES } from './routes'
import { useAuth } from '../auth/AuthContext'
import Icon from '../ui/icons'
import { BrandGlyph } from '../components/BrandMark'

/* "Subway map" sidebar: each group is a coloured line (routes.js
   NAV_GROUP_META `tone`) that starts at a lettered station and runs down
   through its pages, each page a stop. The page you are on is a glowing stop.
   The collapsed icon rail drops the map and shows each page's icon instead. */

/* Dashboard's tile: one dot per line colour. */
const LINE_TONES = ['red', 'amber', 'blue', 'sage', 'mauve', 'slate']

function NavItem({ r, badges }) {
  const count = r.badge ? badges?.[r.badge] : null
  return (
    <NavLink
      to={`/${r.path}`}
      title={r.comingSoon ? `${r.title} — coming soon` : r.title}
      className={({ isActive }) => `nav-item nav-stop-item${r.comingSoon ? ' nav-item-soon' : ''}${isActive ? ' active' : ''}`}
    >
      <span className="nav-stop" aria-hidden="true" />
      <Icon name={r.icon} className="nav-rail-icon" />
      <span className="nav-item-label">{r.title}</span>
      {r.comingSoon && <span className="nav-badge nav-badge-soon">Soon</span>}
      {r.tag && <span className="nav-badge nav-badge-ai">{r.tag}</span>}
      {count ? (
        <span className={`nav-badge${r.badge === 'inbox' || r.badge === 'notifications' ? ' nav-badge-alert' : ''}`}>
          {count}
        </span>
      ) : null}
    </NavLink>
  )
}

function OverviewItem({ r }) {
  return (
    <NavLink
      to={`/${r.path}`}
      title={r.title}
      className={({ isActive }) => `nav-item nav-overview${isActive ? ' active' : ''}`}
    >
      <span className="nav-lines-tile" aria-hidden="true">
        {LINE_TONES.map((t) => <span key={t} className={`nav-tone-${t}`} />)}
      </span>
      <Icon name={r.icon} className="nav-rail-icon" />
      <span className="nav-item-label">{r.title}</span>
    </NavLink>
  )
}

export default function Sidebar({ collapsed, mobileOpen, onToggleCollapse, badges }) {
  const { can } = useAuth()

  // A group renders only if something under it survived the permission
  // filter — otherwise a low-privilege user sees an empty line.
  const visible = ROUTES.filter((r) => !r.hidden && can(r.permission))

  return (
    <aside
      className={`sidebar${collapsed ? ' collapsed' : ''}${mobileOpen ? ' mobile-open' : ''}`}
      id="sidebar"
    >
      <div className="sidebar-brand">
        <div className="brand-logo">
          <BrandGlyph />
        </div>
        <div className="brand-text">
          <span className="brand-name">TalentFlow</span>
          <span className="brand-sub">Utopia Brands · ATS</span>
        </div>
        <button
          className="sidebar-collapse-btn"
          onClick={onToggleCollapse}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon name="chevron-left" />
        </button>
      </div>

      <nav className="sidebar-nav" aria-label="Primary">
        {NAV_GROUPS.map((group) => {
          const items = visible.filter((r) => r.group === group)
          if (!items.length) return null
          const meta = NAV_GROUP_META[group] || {}
          if (meta.headless) {
            return (
              <div key={group} className="nav-group nav-group-headless">
                {items.map((r) => <OverviewItem key={r.path} r={r} />)}
              </div>
            )
          }
          return (
            <div key={group} className={`nav-group nav-line nav-tone-${meta.tone || 'slate'}`}>
              <div className="nav-group-head">
                <span className="nav-station" aria-hidden="true">{group.charAt(0)}</span>
                <span className="nav-group-name">{group}</span>
              </div>
              <div className="nav-group-items">
                {items.map((r) => <NavItem key={r.path} r={r} badges={badges} />)}
              </div>
            </div>
          )
        })}
      </nav>
    </aside>
  )
}
